import { useAppSelector } from "src/stores/redux/hooks";
import { getSelectedNode, getAppStatus, getNodes } from "src/modules/flow-designer/react-flow/helpers/selectors";
import { ChatSuggestion } from "./use-homepage-suggestions";

const CRASHED_STATUSES = ["CRASH", "HALT", "TERMINATED"];
const SOURCE_TYPES = ["SOURCE", "EXTERNALSOURCE"];
const EDITABLE_AS_TQL_STATUSES = ["CREATED", "DEPLOY_FAILED", "INVALID"];

export function useFlowDesignerSuggestions(isActive: boolean): ChatSuggestion[] {
    const selectedNode = useAppSelector(getSelectedNode);
    const appStatus = useAppSelector(getAppStatus);
    const nodes = useAppSelector(getNodes);

    if (!isActive) return [];

    const statusUpper = appStatus?.toUpperCase() ?? "";

    const withEditAsTql = (suggestions: ChatSuggestion[]): ChatSuggestion[] =>
        EDITABLE_AS_TQL_STATUSES.includes(statusUpper) && nodes.length > 0
            ? [
                  ...suggestions,
                  {
                      title: "Edit as TQL",
                      message: "I want to modify this application's components directly by editing its TQL."
                  }
              ]
            : suggestions;

    if (CRASHED_STATUSES.includes(statusUpper)) {
        return [
            {
                title: "Why did this app crash?",
                message: "Why did this app crash? Walk me through the error and steps to recover it."
            },
            { title: "Undeploy this app", message: "Undeploy this application so I can fix it and redeploy." }
        ];
    }

    if (!selectedNode) {
        const hasSource = nodes.some(n => SOURCE_TYPES.includes(n.data?.type?.toUpperCase()));
        const hasTarget = nodes.some(n => n.data?.type?.toUpperCase() === "TARGET");

        if (!hasSource)
            return withEditAsTql([
                { title: "Help me add a source", message: "Help me add a source to this application." }
            ]);
        if (!hasTarget)
            return withEditAsTql([
                { title: "Help me add a target", message: "Help me add a target to this application." }
            ]);

        if (statusUpper === "DEPLOY_FAILED")
            return withEditAsTql([
                {
                    title: "Why did deployment fail?",
                    message: "Why did this app fail to deploy? Explain the error and how to fix it."
                }
            ]);
        if (["DEPLOYED", "STOPPED", "LOAD_BALANCE_STOPPED"].includes(statusUpper))
            return [
                { title: "Start this app", message: "Start this application." },
                { title: "Undeploy this app", message: "Undeploy this application." }
            ];
        if (statusUpper === "QUIESCED")
            return [
                { title: "Start this app", message: "Start this application." },
                { title: "Undeploy this app", message: "Undeploy this application." }
            ];
        if (
            ["RUNNING", "RUNNING_UNTIL_END", "RUNNING_UNTIL_QUIESCE", "APPROVING_QUIESCE", "QUIESCING"].includes(
                statusUpper
            )
        )
            return [
                {
                    title: "Monitor this app",
                    message: "Show me the current throughput, lag, and event counts for this running application."
                },
                { title: "Stop this app", message: "Stop this application." }
            ];
        return withEditAsTql([{ title: "Deploy and start this app", message: "Deploy and start this application." }]);
    }

    return [];
}
